import React, { forwardRef, useContext } from 'react';
import {
  FloatingPortal,
  FloatingFocusManager,
  FloatingOverlay,
  useMergeRefs,
  FloatingArrow,
} from '@floating-ui/react';
import { usePopoverNextContext } from '../../hooks/usePopoverNextContext';
import type { PopoverNextContentProps } from '../../PopoverNext.types';
import { classes } from './Content.classes';
import { APPEND_TO } from '../../PopoverNext.constants';
import { WixDesignSystemContext } from '../../../WixDesignSystemProvider/context';

const Content = forwardRef<HTMLDivElement, PopoverNextContentProps>(
  function Content({ style, ...props }, propRef) {
    const {
      dataHook,
      placement,
      focusManagerEnabled,
      context,
      appendTo,
      portalRoot,
      zIndex,
      popoverStyles,
      interactions,
      open,
      skin,
      transitionStyles,
      transitionStatus,
      overlay,
      style: styleFromUser,
      showArrow,
      contentClassName,
      arrowProps,
    } = usePopoverNextContext();
    const { contextClassName } = useContext(WixDesignSystemContext);
    const ref = useMergeRefs([context.refs.setFloating, propRef]);
    const { className, ...floatingProps } =
      interactions.getFloatingProps(props);

    if (!open) {
      return null;
    }

    const mergedStyles = {
      ...popoverStyles,
      zIndex,
      ...transitionStyles,
      ...styleFromUser,
      ...style,
    };

    const content = (
      <FloatingFocusManager context={context} disabled={!focusManagerEnabled}>
        <>
          {overlay === true ? <FloatingOverlay /> : (overlay ?? null)}
          <div
            ref={ref}
            data-hook={`${dataHook}-content`}
            data-zindex={zIndex}
            className={classes.root(
              {
                skin,
                placement,
                showArrow,
                status:
                  transitionStatus === 'unmounted' ? null : transitionStatus,
              },
              className as string | undefined,
              contextClassName,
            )}
            style={mergedStyles}
            {...floatingProps}
          >
            {showArrow && (
              <FloatingArrow
                {...arrowProps}
                className={classes.arrow({ skin })}
              />
            )}
            <div className={classes.contentWrapper({}, contentClassName)}>
              {props.children}
            </div>
          </div>
        </>
      </FloatingFocusManager>
    );

    if (appendTo === APPEND_TO.window || appendTo === APPEND_TO.viewport) {
      return (
        <FloatingPortal>
          <div
            /*
             * TODO: [remove when major version bump]
             * for old popover click outside to work when new popover is used inside of it
             * (edge case where different WDS versions are used)
             */
            data-hook="popover-portal"
          >
            {content}
          </div>
        </FloatingPortal>
      );
    }

    if (portalRoot) {
      return (
        <FloatingPortal root={portalRoot} preserveTabOrder={false}>
          <div
            /*
             * TODO: [remove when major version bump]
             * for old popover click outside to work when new popover is used inside of it
             * (edge case where different WDS versions are used)
             */
            data-hook="popover-portal"
          >
            {content}
          </div>
        </FloatingPortal>
      );
    }

    return content;
  },
);

export { Content };
